import { createFileRoute } from "@tanstack/react-router";

import { getCompanyAccessToken } from "~/lib/ghl/get-company-access-token";
import {
  GHL_ADMIN_PERMISSION_PRESET,
  _setUserPermissions,
  getRequiredGhlCompanyId,
  ghlPermissionPresetSchema,
  type GhlPermissionPreset,
} from "~/lib/ghl/set-user-permissions";
import { ghlWebhookDataSchema } from "~/lib/schemas/ghl";
import { serverEnv } from "~/lib/server-env";
import { updateCoach } from "~/lib/server/coach";
import { coachForSubAccount } from "~/lib/server/ghl-webhooks";
import { effectiveMembershipPlan } from "~/lib/shared/membership-plan";
import { parseJsonBody } from "~/lib/utils/parse-json-body";

export const Route = createFileRoute("/api/ghl-set-user-permissions/")({
  staticData: { title: "Set GHL User Permissions" },
  server: {
    handlers: {
      POST: async ({ request }: { request: Request }) => {
        const requestApiKey = request.headers.get("X-Api-Key");
        const adminApiKey = serverEnv.API_GHL_SET_ADMIN_PERMISSIONS_KEY;
        const usesStandardApiKey =
          requestApiKey === serverEnv.API_CREATE_ACCOUNT_KEY;
        const usesAdminApiKey =
          Boolean(adminApiKey) && requestApiKey === adminApiKey;

        if (!usesStandardApiKey && !usesAdminApiKey) {
          return new Response(JSON.stringify({ error: "Invalid API key" }), {
            status: 403,
            headers: { "Content-Type": "application/json" },
          });
        }

        const parsedBody = await parseJsonBody(request, ghlWebhookDataSchema);
        if (!parsedBody.success) {
          return parsedBody.response;
        }
        const data = parsedBody.data;
        const requestedPreset = data.customData?.membership_plan;
        const parsedPreset =
          requestedPreset === undefined
            ? null
            : ghlPermissionPresetSchema.safeParse(requestedPreset);
        if (parsedPreset && !parsedPreset.success) {
          return new Response(
            JSON.stringify({ error: "Invalid membership_plan" }),
            {
              status: 400,
              headers: { "Content-Type": "application/json" },
            },
          );
        }
        const isAdminRequest =
          parsedPreset?.data === GHL_ADMIN_PERMISSION_PRESET;

        if (
          (isAdminRequest && !usesAdminApiKey) ||
          (!isAdminRequest && !usesStandardApiKey)
        ) {
          return new Response(JSON.stringify({ error: "Invalid API key" }), {
            status: 403,
            headers: { "Content-Type": "application/json" },
          });
        }

        const locationId = data["Sub-Account ID"];
        const found = await coachForSubAccount(locationId);
        if ("response" in found) return found.response;
        const { coach } = found;

        if (!coach.ghlUserId) {
          return new Response(
            JSON.stringify({ error: "Coach does not have a GHL user ID" }),
            {
              status: 400,
              headers: { "Content-Type": "application/json" },
            },
          );
        }

        // No preset named: this is a refresh, so it reapplies the level the
        // coach is entitled to — the DERIVED one. Reading `membership_plan`
        // here pushed a bundle holder's Pro+ scopes back down to the Standard
        // the column still says. An explicit preset is an admin choosing a
        // level, and still writes the column below.
        const permissionPreset: GhlPermissionPreset = parsedPreset
          ? parsedPreset.data
          : effectiveMembershipPlan(coach);

        if (parsedPreset && permissionPreset !== GHL_ADMIN_PERMISSION_PRESET) {
          await updateCoach(coach.id, { membershipPlan: permissionPreset });
        }

        const result = await _setUserPermissions(
          coach.ghlUserId,
          permissionPreset,
          await getCompanyAccessToken(
            getRequiredGhlCompanyId(),
            serverEnv.GHL_APP_CLIENT_ID,
            serverEnv.GHL_APP_CLIENT_SECRET,
          ),
        );

        if (result.status === "skipped_agency_user") {
          return Response.json(
            {
              message: `The GHL user for ${coach.username} is an agency user, so plan ${permissionPreset} was not applied. Use the Admin preset instead.`,
            },
            { status: 200 },
          );
        }

        return Response.json(
          {
            message:
              permissionPreset === GHL_ADMIN_PERMISSION_PRESET
                ? `User permissions set for ${coach.username} with preset ${permissionPreset}`
                : `User permissions set for ${coach.username} with plan ${permissionPreset}`,
          },
          {
            status: 201,
          },
        );
      },
    },
  },
});
